import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../lib/api';
import type { PodcastEpisode, PodcastShow, PodcastShowDetail, PodcastVoiceRef } from '../lib/api';
import { CardTitle, Icon, Spinner, StatusBadge, Waveform } from '../components/ui';

interface Voice {
  id: string;
  name: string;
  provider: string;
  language: string;
}

const POLL_MS = 3000;
const MAX_SCRIPT_CHARS = 30000;

const SCRIPT_PLACEHOLDER =
  "A: Welcome back to the show! Today we're talking about...\nB: That's right — and there's a lot to cover.";

// Copyable prompt so users can format their content into the A:/B: dialogue
// with ANY external LLM (ChatGPT, Claude, Gemini, ...) instead of Enhance.
function buildLlmPrompt(language: string | null | undefined): string {
  return [
    'You are a professional podcast script writer. Convert the content I provide below into an engaging two-host podcast conversation.',
    '',
    'Host A is the curious guide: introduces topics, asks questions, reacts naturally.',
    'Host B is the knowledgeable expert: explains, elaborates, tells the stories.',
    '',
    'STRICT OUTPUT FORMAT — follow exactly:',
    '- Every line of dialogue starts with "A: " or "B: " at the beginning of the line.',
    '- No other prefixes, no host names, no stage directions, no sound effects, no headers, no markdown, no notes.',
    '- Preserve every key fact and idea from my content. Do not summarize away details.',
    '- Natural spoken style: contractions, brief reactions, real back-and-forth flow.',
    '- Optimize for AI voice synthesis: punctuation that creates natural rhythm, ellipses (...) for dramatic or thoughtful pauses, and sparing ALL-CAPS for words that deserve strong vocal emphasis.',
    ...(language ? [`- Write the entire script in ${language}, with punctuation conventions natural to it.`] : []),
    '- Output ONLY the script, nothing else.',
    '',
    'Here is my content:',
    '',
    '[PASTE YOUR TEXT HERE]',
  ].join('\n');
}

function formatDate(iso: string): string {
  const d = new Date(iso.includes('T') ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || !Number.isFinite(seconds)) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function voiceRef(v: Voice): PodcastVoiceRef {
  return { provider: v.provider, id: v.id, name: v.name };
}

export function Podcasts() {
  const [shows, setShows] = useState<PodcastShow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Selected show (detail view)
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PodcastShowDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Voices for the create modal + host selects
  const [voices, setVoices] = useState<Voice[]>([]);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newVoiceA, setNewVoiceA] = useState('');
  const [newVoiceB, setNewVoiceB] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState('');

  // Add-episode form
  const [episodeTitle, setEpisodeTitle] = useState('');
  const [episodeScript, setEpisodeScript] = useState('');
  const [episodeBusy, setEpisodeBusy] = useState(false);
  const [episodeError, setEpisodeError] = useState('');

  // Episode editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editScript, setEditScript] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [episodeGenBusy, setEpisodeGenBusy] = useState<string | null>(null);
  const [regenConfirm, setRegenConfirm] = useState<string | null>(null);
  const [enhancingId, setEnhancingId] = useState<string | null>(null);
  const [clearAudioConfirm, setClearAudioConfirm] = useState<string | null>(null);
  const [clearingAudioId, setClearingAudioId] = useState<string | null>(null);

  // LLM prompt modal
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);

  // Show-level actions
  const [configBusy, setConfigBusy] = useState<'a' | 'b' | 'language' | null>(null);
  const [actionError, setActionError] = useState('');
  const [deleteShowConfirm, setDeleteShowConfirm] = useState(false);

  // Episode playback (one at a time)
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadShows = useCallback(async () => {
    const res = await api.getPodcasts();
    if (res.success && res.data) {
      setShows(res.data);
    } else if (res.error) {
      setError(res.error.message);
    }
    setLoading(false);
  }, []);

  const loadDetail = useCallback(async (id: string, silent = false) => {
    if (!silent) setDetailLoading(true);
    const res = await api.getPodcast(id);
    if (res.success && res.data) {
      setDetail(res.data);
    } else if (!silent) {
      setActionError(res.error?.message || 'Failed to load podcast');
    }
    if (!silent) setDetailLoading(false);
  }, []);

  useEffect(() => {
    loadShows();
    api.getVoices().then((res) => {
      if (res.success && res.data) setVoices(res.data);
    });
  }, [loadShows]);

  // Poll the open show while any episode is queued/processing.
  const busy =
    !!detail && detail.episodes.some((ep) => ep.status === 'queued' || ep.status === 'processing');

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (selectedId && busy) {
      // Silent reloads only replace `detail` — the inline editor keeps its own
      // editTitle/editScript state, so an open editor is never clobbered.
      pollRef.current = setInterval(() => loadDetail(selectedId, true), POLL_MS);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [selectedId, busy, loadDetail]);

  const openShow = (id: string) => {
    setSelectedId(id);
    setDetail(null);
    setActionError('');
    setEpisodeError('');
    setEditingId(null);
    setDeleteConfirm(null);
    setRegenConfirm(null);
    setClearAudioConfirm(null);
    setDeleteShowConfirm(false);
    setPlayingId(null);
    loadDetail(id);
  };

  const backToList = () => {
    setSelectedId(null);
    setDetail(null);
    setPlayingId(null);
    loadShows();
  };

  const submitCreate = async () => {
    if (createBusy) return;
    setCreateError('');
    if (!newName.trim()) {
      setCreateError('Give your podcast a name.');
      return;
    }
    setCreateBusy(true);
    const va = voices.find((v) => `${v.provider}:${v.id}` === newVoiceA);
    const vb = voices.find((v) => `${v.provider}:${v.id}` === newVoiceB);
    const res = await api.createPodcast({
      name: newName.trim(),
      description: newDescription.trim() || undefined,
      language: newLanguage || va?.language || undefined,
      voice_a: va ? voiceRef(va) : undefined,
      voice_b: vb ? voiceRef(vb) : undefined,
    });
    setCreateBusy(false);
    if (res.success && res.data) {
      setCreateOpen(false);
      setNewName('');
      setNewDescription('');
      setNewVoiceA('');
      setNewVoiceB('');
      setNewLanguage('');
      await loadShows();
      openShow(res.data.id);
    } else {
      setCreateError(res.error?.message || 'Failed to create podcast');
    }
  };

  const submitEpisode = async () => {
    if (!detail || episodeBusy) return;
    setEpisodeError('');
    if (!episodeTitle.trim()) {
      setEpisodeError('Give the episode a title.');
      return;
    }
    if (episodeScript.length > MAX_SCRIPT_CHARS) {
      setEpisodeError('The script is over the character limit.');
      return;
    }
    setEpisodeBusy(true);
    const res = await api.addPodcastEpisode(detail.id, {
      title: episodeTitle.trim(),
      script: episodeScript || undefined,
    });
    setEpisodeBusy(false);
    if (res.success) {
      setEpisodeTitle('');
      setEpisodeScript('');
      loadDetail(detail.id, true);
    } else {
      setEpisodeError(res.error?.message || 'Failed to add episode');
    }
  };

  const startEdit = (episode: PodcastEpisode) => {
    setEditingId(episode.id);
    setEditTitle(episode.title);
    setEditScript(episode.script);
    setActionError('');
  };

  const submitEdit = async () => {
    if (!detail || !editingId || editBusy) return;
    setEditBusy(true);
    const original = detail.episodes.find((ep) => ep.id === editingId);
    const payload: { title?: string; script?: string } = {};
    if (editTitle.trim() && editTitle.trim() !== original?.title) payload.title = editTitle.trim();
    if (editScript !== original?.script) payload.script = editScript;
    if (Object.keys(payload).length === 0) {
      setEditBusy(false);
      setEditingId(null);
      return;
    }
    const res = await api.updatePodcastEpisode(detail.id, editingId, payload);
    setEditBusy(false);
    if (res.success) {
      setEditingId(null);
      loadDetail(detail.id, true);
    } else {
      setActionError(res.error?.message || 'Failed to update episode');
    }
  };

  const clearEpisodeAudio = async (episodeId: string) => {
    if (!detail || clearingAudioId) return;
    setClearAudioConfirm(null);
    setClearingAudioId(episodeId);
    const res = await api.deletePodcastEpisodeAudio(detail.id, episodeId);
    setClearingAudioId(null);
    if (!res.success) {
      setActionError(res.error?.message || 'Failed to clear the episode audio');
    }
    loadDetail(detail.id, true);
  };

  const enhanceOneEpisode = async (episodeId: string) => {
    if (!detail || enhancingId) return;
    setActionError('');
    setEnhancingId(episodeId);
    const res = await api.enhancePodcastEpisode(detail.id, episodeId);
    setEnhancingId(null);
    if (!res.success) {
      setActionError(res.error?.message || 'Failed to enhance the episode');
    }
    loadDetail(detail.id, true);
  };

  const generateOneEpisode = async (episodeId: string) => {
    if (!detail || episodeGenBusy) return;
    setRegenConfirm(null);
    setEpisodeGenBusy(episodeId);
    const res = await api.generatePodcastEpisode(detail.id, episodeId);
    setEpisodeGenBusy(null);
    if (!res.success) {
      setActionError(res.error?.message || 'Failed to start episode generation');
    }
    loadDetail(detail.id, true);
  };

  const removeEpisode = async (episodeId: string) => {
    if (!detail) return;
    const res = await api.deletePodcastEpisode(detail.id, episodeId);
    setDeleteConfirm(null);
    if (res.success) {
      loadDetail(detail.id, true);
    } else {
      setActionError(res.error?.message || 'Failed to delete episode');
    }
  };

  const changeVoice = async (which: 'a' | 'b', value: string) => {
    if (!detail || configBusy) return;
    const v = voices.find((x) => `${x.provider}:${x.id}` === value);
    if (!v) return;
    setActionError('');
    setConfigBusy(which);
    const res = await api.updatePodcast(detail.id, which === 'a' ? { voice_a: voiceRef(v) } : { voice_b: voiceRef(v) });
    setConfigBusy(null);
    if (!res.success) {
      setActionError(res.error?.message || 'Failed to update the voice');
    }
    loadDetail(detail.id, true);
  };

  const changeLanguage = async (value: string) => {
    if (!detail || configBusy || !value) return;
    setActionError('');
    setConfigBusy('language');
    const res = await api.updatePodcast(detail.id, { language: value });
    setConfigBusy(null);
    if (!res.success) {
      setActionError(res.error?.message || 'Failed to update the language');
    }
    loadDetail(detail.id, true);
  };

  const removeShow = async () => {
    if (!detail) return;
    const res = await api.deletePodcast(detail.id);
    if (res.success) {
      backToList();
    } else {
      setActionError(res.error?.message || 'Failed to delete podcast');
      setDeleteShowConfirm(false);
    }
  };

  const togglePlay = (episodeId: string) => {
    const audio = audioRefs.current[episodeId];
    if (!audio) return;
    if (playingId === episodeId) {
      audio.pause();
    } else {
      if (playingId) audioRefs.current[playingId]?.pause();
      audio.play();
    }
  };

  const languages = [...new Set(voices.map((v) => v.language).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b)
  );

  // Voice pickers only offer voices matching the chosen language
  const voicesForLanguage = (lang: string | null | undefined) =>
    lang ? voices.filter((v) => v.language === lang) : voices;

  /* --------------------------------- Loading -------------------------------- */

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={34} />
      </div>
    );
  }

  /* ------------------------------- Detail view ------------------------------ */

  if (selectedId) {
    if (detailLoading || !detail) {
      return (
        <div className="flex items-center justify-center h-64">
          <Spinner size={34} />
        </div>
      );
    }

    const episodes = detail.episodes;
    const completedCount = episodes.filter((ep) => ep.status === 'completed').length;
    const voicesReady = !!detail.voice_a_name && !!detail.voice_b_name;
    const voiceAValue =
      detail.voice_a_provider && detail.voice_a_id ? `${detail.voice_a_provider}:${detail.voice_a_id}` : '';
    const voiceBValue =
      detail.voice_b_provider && detail.voice_b_id ? `${detail.voice_b_provider}:${detail.voice_b_id}` : '';
    const scriptOverLimit = episodeScript.length > MAX_SCRIPT_CHARS;

    return (
      <div className="fx max-w-[1120px] flex flex-col gap-[18px]">
        {/* Completed episode audio elements */}
        {episodes
          .filter((ep) => ep.status === 'completed' && ep.public_url)
          .map((ep) => (
            <audio
              key={ep.id}
              ref={(el) => {
                audioRefs.current[ep.id] = el;
              }}
              src={ep.public_url!}
              onPlay={() => setPlayingId(ep.id)}
              onPause={() => setPlayingId((prev) => (prev === ep.id ? null : prev))}
              onEnded={() => setPlayingId((prev) => (prev === ep.id ? null : prev))}
              className="hidden"
            />
          ))}

        {/* Header */}
        <div className="flex items-start gap-3 flex-wrap">
          <button onClick={backToList} className="btn-ghost !py-2 !px-3.5 !text-[12.5px] flex-none">
            ← All podcasts
          </button>
          <div className="flex-1 min-w-[200px]">
            <h2 className="font-display font-extrabold text-white text-[20px] m-0">{detail.name}</h2>
            {detail.description && <p className="m-0 mt-1 text-mist-400 text-[13.5px]">{detail.description}</p>}
            <p className="m-0 mt-1 text-mist-500 text-[12.5px]">
              {episodes.length} episode{episodes.length === 1 ? '' : 's'} · {completedCount} completed · created{' '}
              {formatDate(detail.created_at)}
            </p>
          </div>
          <div className="flex items-center gap-2.5 flex-none flex-wrap">
            {deleteShowConfirm ? (
              <span className="flex items-center gap-2">
                <button onClick={removeShow} className="text-[12.5px] font-semibold text-[#F0928C] hover:opacity-80">
                  Confirm delete
                </button>
                <button
                  onClick={() => setDeleteShowConfirm(false)}
                  className="text-[12.5px] text-mist-500 hover:text-mist-300"
                >
                  Cancel
                </button>
              </span>
            ) : (
              <button
                onClick={() => setDeleteShowConfirm(true)}
                aria-label="Delete podcast"
                title="Delete podcast"
                className="flex-none text-mist-500 transition-colors hover:text-[#F0928C]"
              >
                <Icon name="trash" size={17} />
              </button>
            )}
          </div>
        </div>

        {actionError && (
          <div className="p-4 rounded-xl text-sm" style={{ background: 'rgba(216, 54, 47, .12)', color: '#F0928C' }}>
            {actionError}
          </div>
        )}

        {/* Hosts / show configuration */}
        <div className="card p-[18px] flex flex-col gap-3">
          <CardTitle>Hosts</CardTitle>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="label">Voice A (first host)</label>
              <select
                value={voiceAValue}
                onChange={(e) => changeVoice('a', e.target.value)}
                disabled={!!configBusy}
                className="input cursor-pointer"
              >
                <option value="" disabled>
                  {detail.voice_a_name ? `${detail.voice_a_name} (current)` : 'Choose a voice…'}
                </option>
                {voicesForLanguage(detail.language).map((v) => (
                  <option key={`${v.provider}:${v.id}`} value={`${v.provider}:${v.id}`}>
                    {v.name} · {v.language} ({v.provider})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Voice B (second host)</label>
              <select
                value={voiceBValue}
                onChange={(e) => changeVoice('b', e.target.value)}
                disabled={!!configBusy}
                className="input cursor-pointer"
              >
                <option value="" disabled>
                  {detail.voice_b_name ? `${detail.voice_b_name} (current)` : 'Choose a voice…'}
                </option>
                {voicesForLanguage(detail.language).map((v) => (
                  <option key={`${v.provider}:${v.id}`} value={`${v.provider}:${v.id}`}>
                    {v.name} · {v.language} ({v.provider})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Language</label>
              <select
                value={detail.language || ''}
                onChange={(e) => changeLanguage(e.target.value)}
                disabled={!!configBusy}
                className="input cursor-pointer"
              >
                <option value="" disabled>
                  Not set
                </option>
                {languages.map((lang) => (
                  <option key={lang} value={lang}>
                    {lang}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="m-0 text-[12px] text-mist-500">
            {voicesReady
              ? 'Every episode in this podcast is generated with these two host voices.'
              : 'Both hosts need a voice before you can generate episodes.'}
          </p>
        </div>

        {/* Episode list */}
        <div className="card p-[18px] flex flex-col gap-3">
          <CardTitle>Episodes</CardTitle>
          {episodes.length === 0 ? (
            <p className="m-0 text-mist-500 text-[13.5px]">No episodes yet — add your first one below.</p>
          ) : (
            <div className="flex flex-col gap-2.5">
              {episodes.map((episode) =>
                editingId === episode.id ? (
                  <div
                    key={episode.id}
                    className="p-4 rounded-[13px] border border-[var(--a-core)] bg-ink-950 flex flex-col gap-3"
                  >
                    <input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      maxLength={200}
                      className="input"
                      placeholder="Episode title"
                    />
                    <textarea
                      value={editScript}
                      onChange={(e) => setEditScript(e.target.value)}
                      rows={10}
                      className="input resize-y font-mono !text-[13px] leading-relaxed"
                      placeholder={SCRIPT_PLACEHOLDER}
                    />
                    <p className="m-0 text-mist-500 text-xs" style={editScript.length > MAX_SCRIPT_CHARS ? { color: '#F0928C' } : undefined}>
                      {editScript.length.toLocaleString()} / {MAX_SCRIPT_CHARS.toLocaleString()} characters
                      {editScript.length > MAX_SCRIPT_CHARS ? ' — over the limit' : ''}
                      {episode.status === 'completed' ? ' — editing the script resets the episode to draft.' : ''}
                    </p>
                    <div className="flex gap-2.5 justify-end">
                      <button onClick={() => setEditingId(null)} disabled={editBusy} className="btn-ghost !py-2 !px-4 !text-xs">
                        Cancel
                      </button>
                      <button
                        onClick={submitEdit}
                        disabled={editBusy || editScript.length > MAX_SCRIPT_CHARS}
                        className="btn-grad !py-2 !px-4 !text-xs inline-flex items-center gap-2 disabled:opacity-40"
                      >
                        {editBusy && <Spinner size={12} />}
                        Save episode
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    key={episode.id}
                    className="flex items-center gap-3.5 px-4 py-[13px] rounded-[13px] border border-ink-800 bg-ink-900 flex-wrap"
                  >
                    {episode.status === 'completed' && episode.public_url ? (
                      <button
                        type="button"
                        onClick={() => togglePlay(episode.id)}
                        aria-label={playingId === episode.id ? 'Pause episode' : 'Play episode'}
                        className="w-9 h-9 flex-none flex items-center justify-center rounded-[9px] border border-ink-600 bg-ink-950 cursor-pointer transition-colors hover:border-ink-500"
                      >
                        <Icon name={playingId === episode.id ? 'pause' : 'play'} size={15} fill="var(--a-core)" />
                      </button>
                    ) : (
                      <span className="w-9 h-9 flex-none flex items-center justify-center rounded-[9px] border border-ink-800 text-mist-500">
                        {episode.status === 'queued' || episode.status === 'processing' ? (
                          <Spinner size={14} />
                        ) : (
                          <Icon name="mic" size={15} />
                        )}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="m-0 text-white text-[14px] font-semibold truncate">{episode.title}</p>
                      <div className="mt-[5px] h-4 overflow-hidden hidden sm:block">
                        <Waveform seed={episode.title || episode.id} active={playingId === episode.id} />
                      </div>
                      {episode.status === 'failed' && episode.error_message && (
                        <p className="m-0 mt-1 text-[12px]" style={{ color: '#F0928C' }}>
                          {episode.error_message}
                        </p>
                      )}
                    </div>
                    <span className="hidden md:block flex-none font-mono text-[12.5px] text-mist-500">
                      {episode.character_count.toLocaleString()} chars
                    </span>
                    {episode.status === 'completed' && (
                      <span className="hidden md:block flex-none font-mono text-[12.5px] text-mist-500">
                        {formatDuration(episode.duration_seconds)}
                      </span>
                    )}
                    <StatusBadge status={episode.status} />
                    <span className="flex items-center gap-2.5 flex-none">
                      {(episode.status === 'draft' || episode.status === 'failed') && (
                        <button
                          type="button"
                          onClick={() => enhanceOneEpisode(episode.id)}
                          disabled={!!enhancingId || !!episodeGenBusy}
                          aria-label={`Enhance ${episode.title}`}
                          title="Turn any pasted content — notes, an article, bullet points — into a full two-host dialogue script"
                          className="btn-ghost inline-flex items-center gap-1.5 !py-1.5 !px-3 !text-[12px] font-semibold disabled:opacity-40"
                        >
                          {enhancingId === episode.id ? <Spinner size={13} /> : <Icon name="spark" size={14} />}
                          {enhancingId === episode.id ? 'Enhancing…' : 'Enhance'}
                        </button>
                      )}
                      {(episode.status === 'draft' || episode.status === 'failed') && (
                        <button
                          type="button"
                          onClick={() => generateOneEpisode(episode.id)}
                          disabled={!!episodeGenBusy || !!enhancingId || !voicesReady}
                          aria-label={`Generate ${episode.title}`}
                          title={voicesReady ? 'Generate this episode' : 'Pick a voice for both hosts first'}
                          className="btn-accent-outline inline-flex items-center gap-1.5 !py-1.5 !px-3 !text-[12px] font-semibold disabled:opacity-40"
                        >
                          {episodeGenBusy === episode.id ? <Spinner size={13} /> : <Icon name="spark" size={14} />}
                          Generate
                        </button>
                      )}
                      {episode.status === 'completed' &&
                        (clearAudioConfirm === episode.id ? (
                          <span className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => clearEpisodeAudio(episode.id)}
                              className="text-[12px] font-semibold text-[#F0928C] hover:opacity-80"
                            >
                              Clear audio
                            </button>
                            <button
                              type="button"
                              onClick={() => setClearAudioConfirm(null)}
                              className="text-[12px] text-mist-500 hover:text-mist-300"
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setClearAudioConfirm(episode.id)}
                            disabled={!!clearingAudioId || !!episodeGenBusy}
                            aria-label={`Clear audio for ${episode.title}`}
                            title="Delete this episode's audio and reset it to draft — the script is kept"
                            className="btn-ghost inline-flex items-center gap-1.5 !py-1.5 !px-3 !text-[12px] font-semibold disabled:opacity-40"
                          >
                            {clearingAudioId === episode.id ? <Spinner size={13} /> : <Icon name="trash" size={13} />}
                            Clear audio
                          </button>
                        ))}
                      {episode.status === 'completed' &&
                        (regenConfirm === episode.id ? (
                          <span className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => generateOneEpisode(episode.id)}
                              className="text-[12px] font-semibold hover:opacity-80"
                              style={{ color: 'var(--a-hi)' }}
                            >
                              Regenerate
                            </button>
                            <button
                              type="button"
                              onClick={() => setRegenConfirm(null)}
                              className="text-[12px] text-mist-500 hover:text-mist-300"
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setRegenConfirm(episode.id)}
                            disabled={!!episodeGenBusy}
                            aria-label={`Regenerate ${episode.title}`}
                            title="Regenerate this episode (uses characters again)"
                            className="text-mist-500 transition-colors hover:text-white disabled:opacity-40"
                          >
                            {episodeGenBusy === episode.id ? <Spinner size={14} /> : <Icon name="refresh" size={14} />}
                          </button>
                        ))}
                      <button
                        type="button"
                        onClick={() => startEdit(episode)}
                        aria-label={`Edit ${episode.title}`}
                        title="Edit episode"
                        className="text-mist-500 transition-colors hover:text-white"
                      >
                        <Icon name="settings" size={15} />
                      </button>
                      {deleteConfirm === episode.id ? (
                        <span className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => removeEpisode(episode.id)}
                            className="text-[12px] font-semibold text-[#F0928C] hover:opacity-80"
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm(null)}
                            className="text-[12px] text-mist-500 hover:text-mist-300"
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(episode.id)}
                          aria-label={`Delete ${episode.title}`}
                          title="Delete episode"
                          className="text-mist-500 transition-colors hover:text-[#F0928C]"
                        >
                          <Icon name="trash" size={15} />
                        </button>
                      )}
                    </span>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Add episode */}
        <div className="card p-[18px] flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle>Add episode</CardTitle>
            <button
              type="button"
              onClick={() => {
                setPromptCopied(false);
                setPromptOpen(true);
              }}
              title="Get a prompt you can paste into ChatGPT, Claude, or any AI to format your text into the A:/B: script"
              className="btn-ghost inline-flex items-center gap-1.5 !py-1.5 !px-3 !text-[12px] font-semibold"
            >
              <Icon name="copy" size={13} />
              Get AI prompt
            </button>
          </div>
          <input
            value={episodeTitle}
            onChange={(e) => setEpisodeTitle(e.target.value)}
            maxLength={200}
            placeholder={`Episode ${episodes.length + 1} title`}
            className="input"
          />
          <textarea
            value={episodeScript}
            onChange={(e) => setEpisodeScript(e.target.value)}
            rows={8}
            placeholder={SCRIPT_PLACEHOLDER}
            className="input resize-y font-mono !text-[13px] leading-relaxed"
          />
          <p className="m-0 text-mist-500 text-xs">
            Lines start with "A:" or "B:" to assign each host's voice; untagged lines continue the previous speaker.
            Enhance can turn plain notes or an article into a full two-host script.
          </p>
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs" style={scriptOverLimit ? { color: '#F0928C' } : undefined}>
              <span className={scriptOverLimit ? '' : 'text-mist-500'}>
                {episodeScript.length.toLocaleString()} / {MAX_SCRIPT_CHARS.toLocaleString()} characters
                {scriptOverLimit ? ' — over the limit' : ''}
              </span>
            </span>
            <button
              onClick={submitEpisode}
              disabled={episodeBusy || !episodeTitle.trim() || scriptOverLimit}
              className="btn-grad !py-2.5 !px-5 !text-xs inline-flex items-center gap-2 disabled:opacity-40"
            >
              {episodeBusy && <Spinner size={12} />}
              <Icon name="plus" size={14} />
              Add episode
            </button>
          </div>
          {episodeError && (
            <p className="m-0 text-[12.5px]" style={{ color: '#F0928C' }}>
              {episodeError}
            </p>
          )}
        </div>
      </div>
    );
  }

  /* -------------------------------- List view ------------------------------- */

  return (
    <div className="fx max-w-[1120px] flex flex-col gap-[18px]">
      <div className="flex items-center gap-3">
        <p className="m-0 text-mist-400 text-[13.5px]">
          Two-host dialogue shows — set up the hosts once, then script and generate episode after episode.
        </p>
        <button
          onClick={() => {
            setCreateError('');
            setCreateOpen(true);
          }}
          className="btn-grad ml-auto flex-none inline-flex items-center gap-2 !py-2.5 !px-[18px] !text-xs"
        >
          <Icon name="plus" size={15} />
          New podcast
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-xl text-sm" style={{ background: 'rgba(216, 54, 47, .12)', color: '#F0928C' }}>
          {error}
        </div>
      )}

      {shows.length === 0 ? (
        <div className="rounded-2xl border border-ink-700 bg-ink-850 py-16 text-center">
          <span className="inline-flex text-mist-500">
            <Icon name="mic" size={38} />
          </span>
          <h3 className="mt-4 text-white font-semibold text-[15px]">No podcasts yet</h3>
          <p className="mt-1 text-mist-400 text-[13.5px]">Create your first podcast to get started.</p>
          <button
            onClick={() => {
              setCreateError('');
              setCreateOpen(true);
            }}
            className="btn-grad mt-6"
          >
            New podcast
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {shows.map((show) => (
            <button
              key={show.id}
              onClick={() => openShow(show.id)}
              className="card lift p-[18px] text-left cursor-pointer flex flex-col gap-3"
            >
              <div className="flex items-start gap-3">
                <span
                  className="w-10 h-10 rounded-[11px] flex-none flex items-center justify-center"
                  style={{ background: 'rgba(var(--a-rgb), .12)', color: 'var(--a-hi)' }}
                >
                  <Icon name="mic" size={19} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="m-0 text-white text-[14.5px] font-bold truncate">{show.name}</p>
                  <p className="m-0 mt-0.5 text-mist-500 text-[12px] truncate">
                    A: {show.voice_a_name || 'not set'} · B: {show.voice_b_name || 'not set'}
                  </p>
                </div>
              </div>
              <p className="m-0 text-mist-500 text-[12px]">
                {show.episode_count || 0} episode{(show.episode_count || 0) === 1 ? '' : 's'} ·{' '}
                {show.completed_count || 0} completed
              </p>
              <p className="m-0 text-mist-500 text-[11.5px]">Created {formatDate(show.created_at)}</p>
            </button>
          ))}
        </div>
      )}

      {/* Create modal — rendered in a portal on document.body: the page root's
          enter animation (transform) would otherwise trap position:fixed inside it */}
      {/* LLM prompt modal */}
      {promptOpen && createPortal(
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          style={{ background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)' }}
          onClick={() => setPromptOpen(false)}
        >
          <div className="min-h-full flex items-center justify-center p-5">
            <div
              className="fx w-full max-w-2xl m-auto rounded-2xl border border-ink-700 bg-ink-850 p-6"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-label="AI formatting prompt"
            >
              <h3 className="font-display font-extrabold text-white text-xl m-0 mb-1">
                Format your script with any AI
              </h3>
              <p className="text-mist-400 text-[13px] m-0 mb-4">
                Copy this prompt into ChatGPT, Claude, Gemini — any AI. Replace the placeholder with your
                text, and it will return a script in the exact A:/B: format this podcast needs. Then paste
                the result into the episode script box.
              </p>
              <pre className="m-0 mb-4 p-4 rounded-xl border border-ink-700 bg-ink-950 text-mist-300 text-[12px] leading-relaxed whitespace-pre-wrap max-h-[45vh] overflow-y-auto font-mono">
                {buildLlmPrompt(detail?.language)}
              </pre>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setPromptOpen(false)} className="btn-ghost">
                  Close
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard
                      .writeText(buildLlmPrompt(detail?.language))
                      .then(() => setPromptCopied(true))
                      .catch(() => setPromptCopied(false));
                  }}
                  className="btn-grad inline-flex items-center gap-2"
                >
                  <Icon name={promptCopied ? 'check' : 'copy'} size={14} />
                  {promptCopied ? 'Copied!' : 'Copy prompt'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {createOpen && createPortal(
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          style={{ background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)' }}
          onClick={() => !createBusy && setCreateOpen(false)}
        >
          <div className="min-h-full flex items-center justify-center p-5">
          <div
            className="fx w-full max-w-md rounded-2xl border border-ink-700 bg-ink-850 p-6"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="New podcast"
          >
            <h3 className="font-display font-extrabold text-white text-xl m-0 mb-1">New podcast</h3>
            <p className="text-mist-400 text-[13px] m-0 mb-5">
              Name your show and pick a voice for each host. You can add episodes next.
            </p>

            <label className="label">Name</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={120}
              placeholder="e.g. The AI Audio Show"
              className="input mb-4"
              autoFocus
            />

            <label className="label">Description (optional)</label>
            <input
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              maxLength={300}
              placeholder="A short note about this podcast"
              className="input mb-4"
            />

            <label className="label">Voice A (first host)</label>
            <select
              value={newVoiceA}
              onChange={(e) => {
                setNewVoiceA(e.target.value);
                // Default the show's language to Voice A's language
                const v = voices.find((x) => `${x.provider}:${x.id}` === e.target.value);
                if (v?.language && !newLanguage) setNewLanguage(v.language);
              }}
              className="input mb-4 cursor-pointer"
            >
              <option value="">Choose later</option>
              {voicesForLanguage(newLanguage).map((v) => (
                <option key={`${v.provider}:${v.id}`} value={`${v.provider}:${v.id}`}>
                  {v.name} · {v.language} ({v.provider})
                </option>
              ))}
            </select>

            <label className="label">Voice B (second host)</label>
            <select
              value={newVoiceB}
              onChange={(e) => setNewVoiceB(e.target.value)}
              className="input mb-4 cursor-pointer"
            >
              <option value="">Choose later</option>
              {voicesForLanguage(newLanguage).map((v) => (
                <option key={`${v.provider}:${v.id}`} value={`${v.provider}:${v.id}`}>
                  {v.name} · {v.language} ({v.provider})
                </option>
              ))}
            </select>

            <label className="label">Language</label>
            <select
              value={newLanguage}
              onChange={(e) => {
                const lang = e.target.value;
                setNewLanguage(lang);
                // Drop voice picks that don't match the new language
                if (lang) {
                  const matches = (val: string) =>
                    voices.some((v) => `${v.provider}:${v.id}` === val && v.language === lang);
                  if (newVoiceA && !matches(newVoiceA)) setNewVoiceA('');
                  if (newVoiceB && !matches(newVoiceB)) setNewVoiceB('');
                }
              }}
              className="input mb-5 cursor-pointer"
            >
              <option value="">Not set</option>
              {languages.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
            <p className="m-0 -mt-3 mb-4 text-[11.5px] text-mist-500">
              Used by Enhance so episode scripts are written in the right language.
            </p>

            {createError && (
              <p className="m-0 mb-4 text-[12.5px]" style={{ color: '#F0928C' }}>
                {createError}
              </p>
            )}

            <div className="flex gap-3 justify-end">
              <button onClick={() => setCreateOpen(false)} disabled={createBusy} className="btn-ghost">
                Cancel
              </button>
              <button
                onClick={submitCreate}
                disabled={createBusy || !newName.trim()}
                className="btn-grad inline-flex items-center gap-2 disabled:opacity-40"
              >
                {createBusy && <Spinner size={14} />}
                Create podcast
              </button>
            </div>
          </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
